import { combineLatest, Observable, of, map } from 'rxjs';
import { Injectable } from '@angular/core';

import { BrandThemesEnum, ImageRatioEnum, PositionEnum, ThemesEnum } from '@seb/web-design-system/core';
import { ArgumentsLayoutEnum, IArgumentWithConfig, IArguments } from '@seb/web-design-system/arguments';

import { ArgumentsComponentInterface, CategoryAdditionalMarketingData, CustomMedia } from '../models';

import {
  ArgumentsImageRatioEnum, ArgumentsLayoutsEnum, ArgumentsLayoutTextConfigEnum, ArgumentsTextConfigEnum,
  CategoryAdditionalMarketingDataTargetEnum
} from '../enums';

import { transformHeadlineLevelToTitleLevel } from './title-level.mapper';
import { HeadingParagraphMapper } from './headingParagraph.mapper';
import { ImageService } from '../services/image.service';
import { ThemesMapper } from './themes.mapper';
import { LinkMapper } from './link.mapper';

@Injectable({
  providedIn: 'root'
})
export class ArgumentMapper {
  textConfig: PositionEnum = PositionEnum.left;

  constructor(
    private imageService: ImageService,
    private headingParagraphMapper: HeadingParagraphMapper,
    private linkMapper: LinkMapper
  ) {}

  categoryAdditionalMarketingDataToIArgument(
    data: CategoryAdditionalMarketingData,
    customMedia?: CustomMedia
  ): IArguments {
    const externalLink = data.target
      ? data.target === CategoryAdditionalMarketingDataTargetEnum.In_new_window
      : undefined;
    const media = customMedia ? customMedia : data.media;
    return {
      secondaryMedia: this.imageService.normalizeMedia(media),
      content: {
        heading: {
          data: {
            title: data.headline
              ? data.headline
              : undefined,
            subtitle: data.subline
              ? data.subline
              : undefined,
            description: data.description
          },
          isUnderlined: data?.isUnderlined ? data?.isUnderlined : false
        },
        footnote: data.footnote,
        links: !!data.urlLink && data.linkName ? [LinkMapper.toLink(data.urlLink, data.linkName, externalLink)] : []
      }
    };
  }

  argumentsComponentToIArgumentWithConfig(cmp: ArgumentsComponentInterface, isUnderlined?: boolean): Observable<IArgumentWithConfig> {
    const themesConfig: ThemesEnum | BrandThemesEnum = ThemesMapper.mapBrandTheme(cmp.theme);
    const mediaContainer$: Observable<CustomMedia> = cmp.mediaContainer
      ? this.imageService.getMediaFromContainer(cmp.mediaContainer)
      : of(undefined);
    const backgroundMediaContainer$: Observable<CustomMedia> = cmp.backgroundMediaContainer
      ? this.imageService.getMediaFromContainer(cmp.backgroundMediaContainer)
      : of(undefined);

    const selectorBackgroundImages$ = cmp.selectorImageComponentsObjects
      ? Object.values(cmp.selectorImageComponentsObjects).map((media) =>
        this.imageService.getMediaFromContainer(media.backgroundMediaContainer)
      )
      : [];

    return combineLatest([mediaContainer$, backgroundMediaContainer$, ...selectorBackgroundImages$]).pipe(
      map(([mediaContainer, backgroundMediaContainer, ...rest]) => {
        const selectorBackground = [...rest];
        const cmpData = {
          ...cmp,
          isUnderlined
        };
        const argument = {
          media: backgroundMediaContainer
            ? this.imageService.normalizeMedia(backgroundMediaContainer)
            : this.imageService.normalizeMedia(cmp.backgroundMedia),
          secondaryMedia: mediaContainer
            ? this.imageService.normalizeMedia(mediaContainer)
            : this.imageService.normalizeMedia(cmp.media),
          mediasSlider: selectorBackground?.length
            ? selectorBackground.map((media) => this.imageService.normalizeMedia(media))
            : undefined,
          mediasThumbnailsSlider: cmp?.selectorImageComponentsObjects
            ? Object.values(cmp?.selectorImageComponentsObjects).map((media) =>
              this.imageService.normalizeMedia(media.thumbnailMedia)
            )
            : undefined,
          content: {
            heading: this.headingParagraphMapper.toHeadingParagraph(cmpData),
            links: this.linkMapper.toCustomButtons(cmp.linksObjects, themesConfig),
            footnote: cmp.footnote
          }
        };
        return {
          contentData: argument,
          themesConfig: themesConfig,
          layoutTextConfig: this.mapLayoutTextConfig(cmp.layoutTextConfig),
          textConfig: this.mapTextConfig(cmp.textConfig),
          imageRatio: this.argumentsImageRatioEnumToImageRatioEnum(cmp.imageRatio),
          isSmall: cmp.isSmall === 'true',
          brand: cmp['brandCorner'],
          hasGradient: cmp.hasGradient === 'true',
          titleLevel: transformHeadlineLevelToTitleLevel(cmp.headlineLevel)
        };
      })
    );
  }

  mapLayoutTextConfig(
    layout: ArgumentsLayoutTextConfigEnum
  ): PositionEnum.left | PositionEnum.right | PositionEnum.center {
    switch (layout) {
      case ArgumentsLayoutTextConfigEnum.CENTER:
        return PositionEnum.center;
      case ArgumentsLayoutTextConfigEnum.LEFT:
        return PositionEnum.left;
      case ArgumentsLayoutTextConfigEnum.RIGHT:
        return PositionEnum.right;
      default:
        return PositionEnum.right;
    }
  }

  mapTextConfig(textConfig: ArgumentsTextConfigEnum): PositionEnum.left | PositionEnum.center | PositionEnum.right {
    switch (textConfig) {
      case ArgumentsTextConfigEnum.LEFT:
        return PositionEnum.left;
      case ArgumentsTextConfigEnum.CENTER:
        return PositionEnum.center;
      case ArgumentsTextConfigEnum.RIGHT:
        return PositionEnum.right;
      default:
        return PositionEnum.left;
    }
  }

  mapLayout(layout: ArgumentsLayoutsEnum): ArgumentsLayoutEnum {
    switch (layout) {
      case ArgumentsLayoutsEnum.DEFAULT:
        return ArgumentsLayoutEnum.default;
      case ArgumentsLayoutsEnum.DIVIDED:
        return ArgumentsLayoutEnum.divided;
      case ArgumentsLayoutsEnum.EMBEDDED:
        return ArgumentsLayoutEnum.embedded;
      default:
        return ArgumentsLayoutEnum.default;
    }
  }

  argumentsImageRatioEnumToImageRatioEnum(argumentsImageRatio: ArgumentsImageRatioEnum): ImageRatioEnum {
    switch (argumentsImageRatio) {
      case ArgumentsImageRatioEnum.R16_9:
        return ImageRatioEnum.r16_9;
      case ArgumentsImageRatioEnum.R1_1:
        return ImageRatioEnum.r1_1;
      case ArgumentsImageRatioEnum.R3_2:
        return ImageRatioEnum.r3_2;
      case ArgumentsImageRatioEnum.R4_3:
        return ImageRatioEnum.r4_3;
      case ArgumentsImageRatioEnum.R9_16:
        return ImageRatioEnum.r9_16;
      default:
        return ImageRatioEnum.default;
    }
  }
}
